import { Injectable, inject } from '@angular/core';
import {
  ProfileDTO,
  UserInfoDTO,
  UserNationalDataResponseDTO,
  UserRegistrationDTO,
  UserRegistrationUpdateDTO
} from 'models/DTOs';
import { BehaviorSubject, Observable, firstValueFrom, map, of, switchMap, tap } from 'rxjs';
import { CoreApiService, StorageData } from '@services/core-api-service';
import { API_URLS } from 'app/modules/core/constants/core.constants';
import { NotificationService } from '../modules/core/services/notification.service';
import { Router } from '@angular/router';

export interface UserProfile extends UserInfoDTO, ProfileDTO { }

@Injectable({
  providedIn: 'root'
})
export class UserService extends CoreApiService {
  private _notificationService = inject(NotificationService);
  private _router = inject(Router);

  userSubject: BehaviorSubject<UserProfile> = new BehaviorSubject(null);

  getUser(externalUserID: string): Observable<UserProfile> {
    this.clearCachedData('user_logedin');
    if (!externalUserID) return of(null);
    return this.get<UserInfoDTO>(`${API_URLS.user}/${externalUserID}`).pipe(
      switchMap(user => {
        let userProfile: UserProfile = { ...(user as any) };
        //we do not store user until he completes registration
        if (user?.dateRegistered) {
          //this._notificationService.showMessage('User loaded.', 1000);

          return this.getProfile(user.clientID).pipe(
            map(prof => {
              userProfile = { ...user, ...prof };
              if (userProfile.phone.includes('1345')) userProfile.phone = userProfile.phone.replace('1345', '');

              const dataToCache: StorageData<UserProfile> = {
                fetchTime: new Date().valueOf(),
                data: userProfile
              };
              localStorage.setItem('user_logedin', JSON.stringify(dataToCache));
              if (!userProfile.nationalIDTypeNumber || !userProfile.nationalIDType)
                this._notificationService.showMessage('User doesn`t have valid national id, and national id type.');

              this.userSubject.next(userProfile);
              return userProfile;
            })
          );
        }

        return of(userProfile);
      })
    );
  }

  getCachedData<T>(data: string): StorageData<T> {
    const cachedData: StorageData<T> = JSON.parse(localStorage.getItem(data));
    return cachedData;
  }

  getCachedUser(externalUserID?: string) {
    const data = this.getCachedData<UserProfile>('user_logedin');
    const cachedUser = data?.data;
    if (cachedUser?.externalUserID === externalUserID) {
      this.userSubject.next(cachedUser);
      return cachedUser;
    }
    return null;
  }

  updateCachedUser(newData: any) {
    const userData = this.getCachedData<UserProfile>('user_logedin');
    let cachedUser = userData.data;
    if (cachedUser) {
      cachedUser = { ...cachedUser, ...newData };
      userData.data = cachedUser;
      localStorage.setItem('user_logedin', JSON.stringify(userData));
      this.userSubject.next(cachedUser);
    }
  }

  clearCachedData(data: string) {
    if (data === 'user_logedin') this.userSubject.next(null);
    localStorage.removeItem(data);
  }

  register(model: UserRegistrationDTO) {
    this.post<string>(`${API_URLS.registerUser}`, model).subscribe(() => {
      this._notificationService.showMessage('User created successfuly', 1000);
      this._router.navigate(['./profile/account-details']);
    });
  }

  update(id: string, model: UserRegistrationUpdateDTO) {
    if (!model.phone.includes('1345')) model.phone = '1345' + model.phone;

    return this.put<UserRegistrationUpdateDTO>(`${API_URLS.user}/${id}`, model).pipe(
      switchMap(() => {
        this._notificationService.showMessage('User updated successfuly.', 2000);
        return this.getUser(id);
      })
    );
  }

  getProfile(id: string) {
    return this.get<ProfileDTO>(`${API_URLS.profile}/${id}`);
  }

  getNationalData(clientID: string) {
    return this.get<UserNationalDataResponseDTO>(`${API_URLS.userNationals}/${clientID}`).pipe(
      tap(data => {
        const { nationalIDType, nationalIDTypeNumber } = data;
        const user: UserProfile = { ...this.userSubject.value, nationalIDType, nationalIDTypeNumber };
        if (user.nationalIDTypeNumber) this._notificationService.showMessage('User national data loaded', 500);
        this.userSubject.next(user);
      })
    );
  }

  uploadImage(clientID: string, image: string): Promise<any> {
  return firstValueFrom(
    this.post<string>(`${API_URLS.images}/${clientID}/upload_image_cropped`, { imageData: image })
  );
}
}
